// src/components/layout/MarketingFooter.jsx
//
// Shared footer for the ungated marketing pages (homepage, /telc-b1, /telc-b2, /try).
//
// WHY IT IS SHARED, AND WHY IT USES <a>
// Each page previously carried its own near-identical copy of this markup, and every
// copy navigated with `<button onClick={navigate(...)}>`. A Googlebot fetch of the
// homepage, /telc-b1 and /de/telc-b1 each found exactly ONE <a href> (a mailto:), and
// /try found none — so the pages that rank had no crawlable path to the pages that
// don't. Consolidating here means the link graph is defined once and cannot drift.

import React from 'react';
import { useTranslation } from 'react-i18next';
import SmartLink from '../ui/SmartLink';
import SocialLinks from './SocialLinks';

const LINK_CLASS =
  'text-[13px] text-stone-500 hover:text-white transition-colors duration-200';
const HEADING_CLASS =
  'text-[11px] font-semibold tracking-[0.12em] uppercase text-stone-500 mb-4';
const HEADING_STYLE = { fontFamily: "'Space Grotesk', sans-serif" };

export default function MarketingFooter() {
  const { t } = useTranslation();

  const quickLinks = [
    { path: '/mock-exams', label: t('footer.allMockExams', 'All mock exams') },
    { path: '/try', label: t('footer.tryFree', 'Free mock exam') },
    { path: '/telc-b1', label: t('footer.telcB1', 'TELC B1 prep') },
    { path: '/telc-b2', label: t('footer.telcB2', 'TELC B2 prep') },
    { path: '/about', label: t('footer.about') },
    { path: '/faq', label: t('footer.faq') },
    { path: '/grammar', label: t('footer.grammar') },
    { path: '/blog', label: t('footer.blog') },
    { path: '/pricing', label: t('nav.pricing') },
  ];

  const legalLinks = [
    { path: '/terms', label: t('footer.terms') },
    { path: '/privacy', label: t('footer.privacy') },
    { path: '/cookies', label: t('footer.cookies') },
  ];

  return (
    <footer className="relative z-10 bg-stone-950 text-stone-400 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-6">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-1">
            <img src="/logo.png" alt="logami" className="h-10 w-auto mb-4 brightness-0 invert opacity-80" />
            <p className="text-[13px] text-stone-500 leading-relaxed mb-3 max-w-[220px]">
              {t('footer.tagline')}
            </p>
            <p className="text-[12px] text-stone-600">
              {t('footer.madeWith')} ♥ {t('footer.inGermany')}
            </p>
            <SocialLinks
              className="mt-5"
              buttonClassName="border-stone-800 text-stone-500 hover:text-white hover:border-stone-600"
            />
          </div>

          {/* Quick links.
              The nine per-exam links that used to sit in their own column here were
              removed on the owner's call — a ten-item list read as a link dump. The
              crawl path into /mock-exams/* is preserved by the single hub link below:
              /en/mock-exams lists all nine, so the cluster is one hop further from the
              homepage rather than orphaned, which is what it was before 2026-08-18
              (GSC positions 32–62 with no inbound internal links at all).
              Do NOT remove the hub link too without putting one in the page body. */}
          <div>
            <h4 className={HEADING_CLASS} style={HEADING_STYLE}>
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <SmartLink to={link.path} className={LINK_CLASS}>
                    {link.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className={HEADING_CLASS} style={HEADING_STYLE}>
              {t('footer.legal')}
            </h4>
            <ul className="space-y-2.5">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <SmartLink to={link.path} className={LINK_CLASS}>
                    {link.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className={HEADING_CLASS} style={HEADING_STYLE}>
              {t('footer.contactUs')}
            </h4>
            <a href="mailto:team@logami.app" className={LINK_CLASS}>
              team@logami.app
            </a>
            <p className="text-[12px] text-stone-600 mt-2">{t('footer.location')}</p>
          </div>
        </div>

        <div className="border-t border-stone-800/60 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-[12px] text-stone-600">
            © {new Date().getFullYear()} Logami. {t('footer.rights')}
          </p>
          <p className="text-[11px] text-stone-700">{t('footer.gdprCompliant')}</p>
        </div>
      </div>
    </footer>
  );
}
